import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useIsMobile } from '../../hooks/use-mobile'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select'
import { CohortFootnote } from './CohortFootnote'
import { DeviationSpendScatter } from './DeviationSpendScatter'
import { SpendRankedList } from './SpendRankedList'
import { useBenchmarkLabels } from './useBenchmarkLabels'
import { STRATA, assetLabel, buildingOf, closedMonths, pick, type BenchmarkPoint } from './benchmark-utils'
import { EmptyState } from '../ui/empty-state'

/**
 * The portfolio view: filters in one row, the cohort footnote, the scatter
 * (desktop) and the ranked list (always — the only view under `md`).
 */
interface Props {
    org: string
    metric: string
    breakdownKey?: string
    windowEnd?: string
    strata?: string
    currency?: string
    locale: string
    onChange: (next: Record<string, string | undefined>) => void
    onOpenBuilding: (uid: string) => void
}

const ALL = '__all__'

export function PortfolioBenchmarkView({ org, metric, breakdownKey, windowEnd, strata, currency, locale, onChange, onOpenBuilding }: Props) {
    const { t } = useTranslation('benchmark')
    const isMobile = useIsMobile()
    const { defs, lang, tradeLabel } = useBenchmarkLabels(org)
    const months = closedMonths(new Date(), 12)

    const query = $api.useQuery('get', '/api/v1/organizations/{orgCode}/benchmark/distribution', {
        params: {
            path: { orgCode: org },
            query: { metric, breakdown_key: breakdownKey, window_end: windowEnd, strata: strata as (typeof STRATA)[number] | undefined, currency },
        },
    })
    const distribution = query.data

    // Pin the cohort the server resolved (the first trade, the most frequent
    // currency) into the URL, so a bookmark reopens the same cohort even after
    // the portfolio's data moves the defaults.
    const resolvedKey = distribution?.breakdown_key || undefined
    const resolvedCurrency = distribution?.currency || undefined
    useEffect(() => {
        const next: Record<string, string | undefined> = {}
        if (!breakdownKey && resolvedKey) next.key = resolvedKey
        if (!currency && resolvedCurrency) next.currency = resolvedCurrency
        if (Object.keys(next).length > 0) onChange(next)
    }, [breakdownKey, currency, resolvedKey, resolvedCurrency, onChange])

    const open =(p: BenchmarkPoint) => onOpenBuilding(buildingOf(p.asset).uid)
    const selectable = defs.filter((d) => d.subject !== 'perimeter')
    const groups: { key: string; items: typeof selectable }[] = [
        { key: 'building', items: selectable.filter((d) => d.subject === 'building') },
        { key: 'equipment', items: selectable.filter((d) => d.subject === 'equipment') },
    ]

    return (
        <div className="space-y-4" data-testid="benchmark-portfolio-view">
            <div className="flex flex-wrap items-center gap-2">
                <Select value={metric} onValueChange={(v) => onChange({ metric: v, key: undefined, currency: undefined })}>
                    <SelectTrigger className="h-9 w-full sm:w-[300px]" data-testid="benchmark-metric-select" aria-label={t('filters.metric')}>
                        <SelectValue placeholder={t('filters.metric')} />
                    </SelectTrigger>
                    <SelectContent>
                        {groups.map((g) =>
                            g.items.length === 0 ? null : (
                                <SelectGroup key={g.key}>
                                    <SelectLabel>{g.key === 'building' ? t('view.asset') : t('strips.equipment')}</SelectLabel>
                                    {g.items.map((d) => (
                                        <SelectItem
                                            key={d.code}
                                            value={d.code}
                                            disabled={!d.live}
                                            title={!d.live ? pick(d.planned_reason, lang) : pick(d.description, lang)}
                                            data-testid={`benchmark-metric-${d.code}`}
                                        >
                                            {pick(d.label, lang)}
                                            {!d.live && <span className="ml-1 text-xs text-muted-foreground">({t('filters.planned')})</span>}
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            ),
                        )}
                    </SelectContent>
                </Select>

                {distribution && distribution.breakdown_keys.length > 0 && (
                    <Select value={distribution.breakdown_key} onValueChange={(v) => onChange({ key: v })}>
                        <SelectTrigger className="h-9 w-full sm:w-[200px]" data-testid="benchmark-key-select" aria-label={t('filters.breakdown_key')}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {distribution.breakdown_keys.map((k) => (
                                <SelectItem key={k} value={k} data-testid={`benchmark-key-${k}`}>
                                    {tradeLabel(k)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}

                <Select value={windowEnd ?? months[0]} onValueChange={(v) => onChange({ window_end: v === months[0] ? undefined : v })}>
                    <SelectTrigger className="h-9 w-[160px]" data-testid="benchmark-window-select" aria-label={t('filters.window')}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {months.map((m) => (
                            <SelectItem key={m} value={m}>
                                {new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date(`${m}-15`))}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={strata ?? ALL} onValueChange={(v) => onChange({ strata: v === ALL ? undefined : v })}>
                    <SelectTrigger className="h-9 w-[180px]" data-testid="benchmark-strata-select" aria-label={t('filters.strata')}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>{t('filters.strata_all')}</SelectItem>
                        {STRATA.map((s) => (
                            <SelectItem key={s} value={s} data-testid={`benchmark-strata-${s}`}>
                                {t(`strata.${s}`)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {distribution && distribution.currencies.length > 1 && (
                    <Select value={distribution.currency} onValueChange={(v) => onChange({ currency: v })}>
                        <SelectTrigger className="h-9 w-[110px]" data-testid="benchmark-currency-select" aria-label={t('filters.currency')}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {distribution.currencies.map((c) => (
                                <SelectItem key={c} value={c}>
                                    {c}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>

            {query.isLoading && <div className="h-80 animate-pulse rounded-md bg-muted" />}
            {query.isError && <p className="text-sm text-destructive">{t('error')}</p>}

            {distribution && (
                <>
                    <CohortFootnote cohort={distribution.cohort} window={distribution.window} locale={locale} />

                    {distribution.points.length === 0 ? (
                        <EmptyState
                            variant="inline"
                            live
                            title={t('scatter.empty')}
                            data-testid="benchmark-empty"
                        />
                    ) : (
                        <>
                            {!isMobile && distribution.stats && (
                                <Card>
                                    <CardHeader className="pb-0">
                                        <CardTitle className="text-base">{t('scatter.title')}</CardTitle>
                                        <p className="text-xs text-muted-foreground">{t('scatter.subtitle')}</p>
                                    </CardHeader>
                                    <CardContent data-testid="benchmark-scatter">
                                        <DeviationSpendScatter distribution={distribution} locale={locale} onSelect={open} />
                                    </CardContent>
                                </Card>
                            )}
                            <SpendRankedList distribution={distribution} locale={locale} onSelect={open} />
                        </>
                    )}

                    {distribution.excluded_assets.length > 0 && (
                        <details className="rounded-md border px-3 py-2 text-sm" data-testid="benchmark-excluded">
                            <summary className="cursor-pointer text-muted-foreground">
                                {t('cohort.excluded_list_title')} ({distribution.excluded_assets.length})
                            </summary>
                            <ul className="mt-2 space-y-1">
                                {distribution.excluded_assets.map((e) => (
                                    <li key={e.asset.uid} className="flex justify-between gap-3">
                                        <span className="truncate text-foreground">{assetLabel(e.asset)}</span>
                                        <span className="shrink-0 text-muted-foreground">{t(`reason.${e.reason}`)}</span>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </>
            )}
        </div>
    )
}
